import {
  PlacementApprovalStatus,
  PlacementPricingType,
  PlacementType,
  PlacementXpParameter,
  type Placement,
  type PlacementDemandIntegration,
} from './model';
import { emptyPlacementFormValue, type PlacementFormValue } from './placementFormSchema';

export function placementToFormValue(placement: Placement): PlacementFormValue {
  const value = emptyPlacementFormValue(placement.type);
  return {
    ...value,
    status: placement.status,
    name: placement.name,
    publisherId: placement.publishersIds[0] ?? placement.publishers[0]?.id ?? '',
    deliveryType: placement.deliveryType ?? value.deliveryType,
    position: placement.position ?? '',
    bidFloor: toInput(placement.bidFloor),
    qpsLimit: toInput(placement.qpsLimit),
    maxAdDuration: toInput(placement.maxAdDuration),
    shareOfVoice: toInput(placement.shareOfVoice),
    notes: placement.notes ?? '',
    debug: placement.debug ?? false,
    ignoreCompliance: placement.ignoreCompliance ?? false,
    passToDemandAppData: placement.passToDemandAppData ?? false,
    passToDemandAppStoreIds: placement.passToDemandAppStoreIds ?? false,
    blockBidsWithoutCatOrAdomain: placement.blockBidsWithoutCatOrAdomain ?? false,
    ivtEnabled: placement.ivtEnabled ?? false,
    ivtType: placement.ivtType ?? '',
    ivtIntegrationType: placement.ivtIntegrationType ?? '',
    ivtSamplingPercentage: toInput(placement.ivtSamplingPercentage),
    ivtGeneralRejectPercentage: toInput(placement.ivtGeneralRejectPercentage),
    ivtSophisticatedRejectPercentage: toInput(placement.ivtSophisticatedRejectPercentage),
    impressionTrackers: placement.impressionTrackers.map((item) => ({
      tracker: item.tracker,
      sampleRate: toInput(item.sampleRate),
    })),
    upstreamCpmCapEnabled: placement.upstreamCpmCapEnabled ?? false,
    upstreamCpmCapMinCpm: toInput(placement.upstreamCpmCapMinCpm),
    upstreamCpmCapMaxCpm: toInput(placement.upstreamCpmCapMaxCpm),
    rewriteVastEnabled: placement.rewriteVastEnabled ?? false,
    rewriteVastVersion: toInput(placement.rewriteVastVersion ?? 4),
    schainIgnoreRequest: placement.schainIgnoreRequest ?? false,
    schainPreserveFirstOnly: placement.schainPreserveFirstOnly ?? false,
    poddingEnabled: placement.poddingEnabled ?? false,
    maxPodSlotCount: toInput(placement.maxPodSlotCount),
    podFloor: toInput(placement.podFloor),
    minPodDuration: toInput(placement.minPodDuration),
    maxPodDuration: toInput(placement.maxPodDuration),
    deduplicateUniqueMediaFile: placement.deduplicateUniqueMediaFile ?? false,
    deduplicateUniqueAdvertiserDomain: placement.deduplicateUniqueAdvertiserDomain ?? false,
    deduplicateUniqueIABCategory: placement.deduplicateUniqueIABCategory ?? false,
    pricingType: placement.pricingType ?? PlacementPricingType.None,
    pricing: toInput(
      placement.pricingType === PlacementPricingType.RevenueShare && placement.pricing !== undefined
        ? Number((placement.pricing * 100).toFixed(10))
        : placement.pricing,
    ),
    lineItemMargin: toInput(placement.lineItemMargin),
    demandIntegrations: placement.placementDemandIntegrations,
    targetings: placement.targetingList,
    exclusionsEnabled: placement.exclusionsEnabled ?? false,
    advertiserDomainsText: placement.advertiserDomains.join('\n'),
    excludedCreativeIdsText: placement.excludedCreativeIds.join('\n'),
    excludedIabCategoryIds: placement.iabCategoriesExcluded,
    bcAvEnabled: placement.bcAvEnabled ?? false,
    bcVideoEnabled: placement.bcVideoEnabled ?? false,
    bcAlwaysUseCache: placement.bcAlwaysUseCache ?? false,
    stitcherEnabled: placement.stitcherEnabled ?? false,
    stAcceptPercentageOfFloor: toInput(placement.stAcceptPercentageOfFloor),
    stMaxBids: toInput(placement.stMaxBids ?? 2),
    stMaxAvBids: toInput(placement.stMaxAvBids ?? 0),
    stMaxVideoBids: toInput(placement.stMaxVideoBids ?? 1),
    stMaxBidsFromCache: toInput(placement.stMaxBidsFromCache ?? 0),
    stIgnoreAdDuration: placement.stIgnoreAdDuration ?? false,
    stPoddingFloor: toInput(placement.stPoddingFloor),
    stFeeCpm: toInput(placement.stFeeCpm),
    stDedupeEnabled: placement.stDedupeEnabled ?? false,
    stDedupeUniqueMediaFile: placement.stDedupeUniqueMediaFile ?? false,
    stDedupeUniqueAdvertiserDomain: placement.stDedupeUniqueAdvertiserDomain ?? false,
    stDedupeUniqueIABCategory: placement.stDedupeUniqueIABCategory ?? false,
    dmEnabled: placement.dmEnabled ?? false,
    dmShortTimeoutMs: toInput(placement.dmShortTimeoutMs),
    dmLongTimeoutMs: toInput(placement.dmLongTimeoutMs),
    adUnit: placementAdUnitToFormValue(placement, value.adUnit),
    overridePublisherXpParametersMapping: placement.overridePublisherXpParametersMapping ?? false,
    xpParametersMappings: placement.xpParametersMappings.map((item) => ({
      value: item.value,
      xpParameter: isXpParameter(item.xpParameter) ? item.xpParameter : '',
    })),
  };
}

export function serializePlacementFormValue(value: PlacementFormValue) {
  const pricing = optionalNumber(value.pricing);
  return {
    type: value.type,
    status: value.status,
    name: value.name.trim(),
    publishersIds: [value.publisherId],
    deliveryType: value.deliveryType,
    position: value.position || null,
    bidFloor: optionalNumber(value.bidFloor),
    qpsLimit: optionalNumber(value.qpsLimit),
    maxAdDuration: optionalNumber(value.maxAdDuration),
    shareOfVoice: optionalNumber(value.shareOfVoice),
    notes: value.notes || null,
    debug: value.debug,
    ignoreCompliance: value.ignoreCompliance,
    passToDemandAppData: value.passToDemandAppData,
    passToDemandAppStoreIds: value.passToDemandAppStoreIds,
    blockBidsWithoutCatOrAdomain: value.blockBidsWithoutCatOrAdomain,
    ivtEnabled: value.ivtEnabled,
    ivtType: value.ivtEnabled ? value.ivtType || null : null,
    ivtIntegrationType: value.ivtEnabled ? value.ivtIntegrationType || null : null,
    ivtSamplingPercentage: optionalNumber(value.ivtSamplingPercentage),
    ivtGeneralRejectPercentage: optionalNumber(value.ivtGeneralRejectPercentage),
    ivtSophisticatedRejectPercentage: optionalNumber(value.ivtSophisticatedRejectPercentage),
    impressionTrackers: value.impressionTrackers.map((item) => ({
      tracker: item.tracker,
      sampleRate: Number(item.sampleRate),
    })),
    upstreamCpmCapEnabled: value.upstreamCpmCapEnabled,
    upstreamCpmCapMinCpm: optionalNumber(value.upstreamCpmCapMinCpm),
    upstreamCpmCapMaxCpm: optionalNumber(value.upstreamCpmCapMaxCpm),
    rewriteVastEnabled: value.rewriteVastEnabled,
    rewriteVastVersion: optionalNumber(value.rewriteVastVersion),
    schainIgnoreRequest: value.schainIgnoreRequest,
    schainPreserveFirstOnly: value.schainPreserveFirstOnly,
    poddingEnabled: value.poddingEnabled,
    maxPodSlotCount: optionalNumber(value.maxPodSlotCount),
    podFloor: optionalNumber(value.podFloor),
    minPodDuration: optionalNumber(value.minPodDuration),
    maxPodDuration: optionalNumber(value.maxPodDuration),
    deduplicateUniqueMediaFile: value.deduplicateUniqueMediaFile,
    deduplicateUniqueAdvertiserDomain: value.deduplicateUniqueAdvertiserDomain,
    deduplicateUniqueIABCategory: value.deduplicateUniqueIABCategory,
    pricingType: value.pricingType,
    pricing:
      value.pricingType === PlacementPricingType.RevenueShare && pricing !== null
        ? pricing / 100
        : pricing,
    lineItemMargin: optionalNumber(value.lineItemMargin),
    placementDemandIntegrations: value.demandIntegrations.map(normalizePlacementDemandIntegration),
    targetingList: value.targetings,
    exclusionsEnabled: value.exclusionsEnabled,
    advertiserDomains: parseSeparatedValues(value.advertiserDomainsText),
    excludedCreativeIds: parseSeparatedValues(value.excludedCreativeIdsText),
    iabCategoriesExcluded: value.excludedIabCategoryIds,
    bcAvEnabled: value.bcAvEnabled,
    bcVideoEnabled: value.bcVideoEnabled,
    bcAlwaysUseCache: value.bcAlwaysUseCache,
    stitcherEnabled: value.stitcherEnabled,
    stAcceptPercentageOfFloor: optionalNumber(value.stAcceptPercentageOfFloor),
    stMaxBids: optionalNumber(value.stMaxBids),
    stMaxAvBids: optionalNumber(value.stMaxAvBids),
    stMaxVideoBids: optionalNumber(value.stMaxVideoBids),
    stMaxBidsFromCache: optionalNumber(value.stMaxBidsFromCache),
    stIgnoreAdDuration: value.stIgnoreAdDuration,
    stPoddingFloor: optionalNumber(value.stPoddingFloor),
    stFeeCpm: optionalNumber(value.stFeeCpm),
    stDedupeEnabled: value.stDedupeEnabled,
    stDedupeUniqueMediaFile: value.stDedupeUniqueMediaFile,
    stDedupeUniqueAdvertiserDomain: value.stDedupeUniqueAdvertiserDomain,
    stDedupeUniqueIABCategory: value.stDedupeUniqueIABCategory,
    dmEnabled: value.dmEnabled,
    dmShortTimeoutMs: optionalNumber(value.dmShortTimeoutMs),
    dmLongTimeoutMs: optionalNumber(value.dmLongTimeoutMs),
    adUnit: value.type === PlacementType.AdUnit ? serializeAdUnit(value) : undefined,
    overridePublisherXpParametersMapping: value.overridePublisherXpParametersMapping,
    xpParametersMappings: value.xpParametersMappings.filter(
      (row) => row.value.trim() && row.xpParameter,
    ),
  };
}

export type PlacementMutationPayload = ReturnType<typeof serializePlacementFormValue>;

export function normalizePlacementDemandIntegration(
  integration: PlacementDemandIntegration,
): PlacementDemandIntegration {
  if (integration.demandIntegrationId) {
    return {
      ...integration,
      approvalStatus: integration.approvalStatus ?? PlacementApprovalStatus.Pending,
    };
  }

  return {
    demandIntegrationId: integration.id ?? '',
    demandIntegrationName: integration.name ?? integration.dpDisplayName,
    placementId: undefined,
    approvalStatus: integration.approvalStatus ?? PlacementApprovalStatus.Pending,
    duplication: integration.duplicateDemandIntegration ?? false,
    revenuePct: 0,
    stats: { global7daysRevenuePct: 0 },
    integrationType: integration.integrationType,
    bidAdjustment: null,
    bidFloor: null,
    unitActivated: null,
    bidType: null,
  };
}

function placementAdUnitToFormValue(placement: Placement, defaults: PlacementFormValue['adUnit']) {
  return {
    ...defaults,
    name: placement.adUnit?.name ?? '',
    widgetName: placement.adUnit?.widgetName ?? '',
    publisherDomainIds: placement.adUnit?.publisherDomains.map((item) => item.domainId) ?? [],
    video: placement.adUnit?.video ?? false,
    adWidth: toInput(placement.adUnit?.adWidth),
    adHeight: toInput(placement.adUnit?.adHeight),
    devicePlacement: placement.adUnit?.devicePlacement ?? '',
    s2s: placement.adUnit?.s2s ?? false,
    bidder: placement.adUnit?.bidder ?? false,
    viewIn: placement.adUnit?.unitConfig?.viewIn ?? '',
    viewOut: placement.adUnit?.unitConfig?.viewOut ?? '',
    adLoadDelay: toInput(placement.adUnit?.unitConfig?.adLoadDelay),
    closeButton: placement.adUnit?.unitConfig?.closeButton ?? false,
    adhesion: placement.adUnit?.unitConfig?.adhesion ?? false,
    noFillHideUnit: placement.adUnit?.unitConfig?.noFillHideUnit ?? false,
    idSystemIdentityLink: placement.adUnit?.unitConfig?.idSystemIdentityLink ?? false,
    ibv: placement.adUnit?.unitConfig?.ibv ?? false,
    refreshRate: toInput(placement.adUnit?.unitConfig?.refreshRate),
    maxRefresh: toInput(placement.adUnit?.unitConfig?.maxRefresh),
    weMakeSticky: placement.adUnit?.unitConfig?.weMakeSticky ?? false,
  };
}

function serializeAdUnit(value: PlacementFormValue) {
  return {
    name: value.adUnit.name || null,
    widgetName: value.adUnit.widgetName,
    publisherDomains: value.adUnit.publisherDomainIds.map((domainId) => ({
      publisherId: value.publisherId,
      domainId,
    })),
    video: value.adUnit.video,
    adWidth: optionalNumber(value.adUnit.adWidth),
    adHeight: optionalNumber(value.adUnit.adHeight),
    devicePlacement: value.adUnit.devicePlacement || null,
    s2s: value.adUnit.s2s,
    bidder: value.adUnit.bidder,
    unitConfig: {
      viewIn: value.adUnit.viewIn || null,
      viewOut: value.adUnit.viewOut || null,
      adLoadDelay: optionalNumber(value.adUnit.adLoadDelay),
      closeButton: value.adUnit.closeButton,
      adhesion: value.adUnit.adhesion,
      noFillHideUnit: value.adUnit.noFillHideUnit,
      idSystemIdentityLink: value.adUnit.idSystemIdentityLink,
      ibv: value.adUnit.ibv,
      refreshRate: optionalNumber(value.adUnit.refreshRate),
      maxRefresh: optionalNumber(value.adUnit.maxRefresh),
      weMakeSticky: value.adUnit.weMakeSticky,
    },
  };
}

export function parsePlacementSeparatedValues(value: string) {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseSeparatedValues(value: string) {
  return parsePlacementSeparatedValues(value);
}

function optionalNumber(input: string) {
  return input.trim() ? Number(input) : null;
}

function toInput(value: number | undefined) {
  return value === undefined ? '' : String(value);
}

function isXpParameter(value: string): value is PlacementXpParameter {
  return Object.values(PlacementXpParameter).includes(value as PlacementXpParameter);
}
